/** A small display-only Markdown subset. No HTML, URLs or executable actions. */
export function workspaceTextLines(content: string) {
  return content.split('\n').map(line => {
    const heading = /^\s{0,3}#{1,4}\s+(.+)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.+)$/.exec(line);
    return { text: heading ? heading[1] : bullet ? `• ${bullet[1]}` : line, heading: !!heading, blank: !line.trim() };
  });
}

export function workspaceTextSpans(content: string) {
  const spans: Array<{ text: string; emphasis?: boolean; code?: boolean }> = [];
  const pattern = /\*\*([^*\n]+)\*\*|`([^`\n]+)`/g;
  let cursor = 0;
  for (const match of content.matchAll(pattern)) {
    const index = match.index!;
    if (index > cursor) spans.push({ text: content.slice(cursor, index) });
    spans.push({ text: match[1] || match[2], ...(match[1] ? { emphasis: true } : { code: true }) });
    cursor = index + match[0].length;
  }
  if (cursor < content.length) spans.push({ text: content.slice(cursor) });
  return spans;
}

export type WorkspaceTextBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'bullets'; items: string[] }
  | { type: 'numbers'; items: string[]; start: number }
  | { type: 'table'; header: string[]; rows: string[][] }
  | { type: 'blank' };

const cells = (line: string) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
const isSeparator = (line: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);

/**
 * The reply as display blocks (same display-only Markdown subset, plus numbered lists and
 * pipe tables): headings, paragraphs, bullet / numbered lists and tables. Text stays text —
 * spans are read with workspaceTextSpans.
 */
export function workspaceTextBlocks(content: string): WorkspaceTextBlock[] {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks: WorkspaceTextBlock[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) { if (blocks.length && blocks[blocks.length - 1].type !== 'blank') blocks.push({ type: 'blank' }); continue; }
    // A pipe table: a row, a --- separator, then rows.
    if (line.includes('|') && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      const header = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        // A cell the model wrapped onto the next line ("| 5 | **نايف" + "موافقة** | …") is one row.
        let line = lines[i];
        while (cells(line).length < header.length && i + 1 < lines.length && lines[i + 1].trim() && !isSeparator(lines[i + 1])) line = `${line} ${lines[++i].trim()}`;
        rows.push(cells(line)); i++;
      }
      i--;
      blocks.push({ type: 'table', header, rows });
      continue;
    }
    const heading = /^\s{0,3}#{1,4}\s+(.+)$/.exec(line);
    if (heading) { blocks.push({ type: 'heading', text: heading[1] }); continue; }
    const bullet = /^\s*[-*•]\s+(.+)$/.exec(line);
    if (bullet) {
      const last = blocks[blocks.length - 1];
      if (last?.type === 'bullets') last.items.push(bullet[1]); else blocks.push({ type: 'bullets', items: [bullet[1]] });
      continue;
    }
    const numbered = /^\s*(\d{1,3})[.)]\s+(.+)$/.exec(line);
    if (numbered) {
      const last = blocks[blocks.length - 1];
      if (last?.type === 'numbers') last.items.push(numbered[2]); else blocks.push({ type: 'numbers', items: [numbered[2]], start: Number(numbered[1]) || 1 });
      continue;
    }
    blocks.push({ type: 'paragraph', text: line.trim() });
  }
  while (blocks.length && blocks[blocks.length - 1].type === 'blank') blocks.pop();
  return blocks;
}
